/**
 * Controlador principal para el módulo de subcategorías.
 *
 * Este controlador maneja toda la lógica de negocio para la gestión de subcategorías,
 * incluyendo operaciones CRUD (Crear, Leer, Actualizar, Eliminar), validaciones de formularios,
 * paginación, filtrado y comunicación con servicios del backend. Proporciona una interfaz
 * completa para administrar subcategorías dentro de una categoría específica.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { useEffect, useState, useRef } from "react";
import Spinner from "components/MDSpinner";
import MDAlert from "components/Alert";
import { useNavigate, useParams } from "react-router-dom";
import FilterController from "components/filter/controller/FilterController";
import SubCategoryList from "../modules/SubCategoryList";
import { SubCategoryCreate } from "../modules/SubCategoryCreate";
import {
  SubCategoryCreateService,
  SubCategoryUpdateService,
  SubCategoryDeleteService,
  CategoryGetByIdService,
} from "../services/SubCategoryService";
import { SubCategoryEdit } from "../modules/SubCategoryEdit";
import { SubCategoryDelete } from "../modules/SubCategoryDelete";

export default function SubCategoryController() {
  const { id: categoryId } = useParams();
  const navigate = useNavigate();
  const filterControllerRef = useRef();

  // Configuración del filtro para subcategorías
  const filterConfig = [
    { visibleName: "Nombre", dbField: "name", type: "text" },
    { visibleName: "Estado", dbField: "isEnabled", type: "select", source: "enabledTypes" },
    { visibleName: "Creado", dbField: "created", type: "date" },
    { visibleName: "Modificado", dbField: "modified", type: "date" },
    { visibleName: "Creado por", dbField: "created_by_id", type: "text" },
    { visibleName: "Modificado por", dbField: "modified_by_id", type: "text" },
  ];

  const dataTable = "subcategories";

  // Variables de creación de subcategoría
  const [openCreate, setOpenCreate] = useState(false);
  // Variables de edición de subcategoría
  const [openEdit, setOpenEdit] = useState(false);
  // Variables de eliminación de subcategoría
  const [openDelete, setOpenDelete] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState(null);
  const [categoryName, setCategoryName] = useState("");

  // Estado de validaciones
  const [isFieldValid, setIsFieldValid] = useState({
    name: true,
    isEnabled: true,
  });

  // Patrones de validación
  const validationPatterns = {
    name: /^[a-zA-ZÀ-ÿ\s\u00f1\u00d1,;.:()\-]+$/, // Permite letras, espacios y caracteres especiales comunes (coma, punto y coma, punto, dos puntos, paréntesis, guión)
  };

  // Variables de paginación
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesStart, setEntriesStart] = useState(0);
  const [entriesEnd, setEntriesEnd] = useState(0);

  // Variables de listado
  const [subCategoriesList, setSubCategoriesList] = useState([]);

  // Controladores comunes
  const [isDisabled, setIsDisabled] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);
  const [recoveredData, setRecoveredData] = useState({});
  const [editedData, setEditedData] = useState({});
  const [newData, setNewData] = useState({});
  const [loading, setLoading] = useState(false);
  const [typeAlert, setTypeAlert] = useState("");
  const [message, setMessage] = useState("");
  const [isAlert, setIsAlert] = useState(false);

  const closeSubCategoryCreate = () => setOpenCreate(false);
  const closeSubCategoryEdit = () => setOpenEdit(false);
  const closeSubCategoryDelete = () => setOpenDelete(false);

  /**
   * Obtiene el valor de un campo del formulario
   * @param {string} fieldName - Nombre del campo
   * @returns {string} Valor del campo
   */
  const getFieldValueNew = (fieldName) => {
    return newData[fieldName];
  };

  /**
   * Maneja el cambio de valor en un campo del formulario
   * @param {string} fieldName - Nombre del campo
   * @param {string} value - Nuevo valor
   */
  const handleFieldChangeNew = (fieldName, value) => {
    setNewData({
      ...newData,
      [fieldName]: value,
    });
    handleChangeValid(fieldName, value);
  };

  /**
   * Obtiene el valor de un campo para edición
   * @param {string} fieldName - Nombre del campo
   * @returns {any} Valor del campo
   */
  const getFieldValue = (fieldName) => {
    return editedData[fieldName] !== undefined ? editedData[fieldName] : recoveredData[fieldName];
  };

  /**
   * Maneja el cambio de valor en un campo de edición
   * @param {string} fieldName - Nombre del campo
   * @param {any} value - Nuevo valor
   */
  const handleFieldChange = (fieldName, value) => {
    setEditedData({
      ...editedData,
      [fieldName]: value,
    });
    setIsFormModified(true);
    handleChangeValid(fieldName, value);
  };

  /**
   * Maneja las alertas del sistema
   * @param {string} typeAlert - Tipo de alerta
   * @param {string} message - Mensaje de la alerta
   */
  const handleAlert = (typeAlert, message) => {
    setTypeAlert(typeAlert);
    setMessage(message);
    setIsAlert(true);
  };

  /**
   * Convierte todos los valores del objeto a string
   * @param {Object} objeto - Objeto a convertir
   * @returns {Object} Objeto con valores convertidos a string
   */
  function convertValuesToString(objeto) {
    const convertedObject = {};
    for (const clave in objeto) {
      if (objeto.hasOwnProperty(clave)) {
        convertedObject[clave] = String(objeto[clave]);
      }
    }
    return convertedObject;
  }

  /**
   * Maneja el envío del formulario de creación de subcategoría
   * @param {Event} e - Evento del formulario
   */
  const submitSubCategoryCreate = async (e) => {
    e.preventDefault();
    const hasErrors = hasInvalidFields(isFieldValid);

    if (hasErrors) {
      handleAlert("fail", "Hay al menos un error en los campos ingresados");
      return;
    }

    setLoading(true);
    const convertedObject = convertValuesToString({
      ...newData,
      category_id: categoryId,
    });
    const result = await SubCategoryCreateService(convertedObject);

    if (result.success) {
      await filterControllerRef.current.handleClickSubmit();
      handleAlert("success", "La subcategoría se ha creado correctamente");
    } else {
      handleAlert("fail", result.message);
    }
    closeSubCategoryCreate();
    setLoading(false);
  };

  /**
   * Ejecuta la búsqueda/filtrado
   */
  const callHandleClickSubmit = () => {
    if (filterControllerRef.current) {
      filterControllerRef.current.handleClickSubmit();
    }
  };

  /**
   * Maneja la apertura del modal de creación
   * @param {Event} event - Evento del botón
   */
  const handleSubCategoryCreate = (event) => {
    event.preventDefault();
    resetFieldValid();
    setIsDisabled(false);
    const data = {
      name: "",
    };
    setNewData(data);
    setOpenCreate(true);
  };

  /**
   * Resetea las validaciones de los campos
   */
  const resetFieldValid = () => {
    setIsFieldValid({
      name: true,
      isEnabled: true,
    });
  };

  /**
   * Valida un campo contra su patrón de expresión regular
   * @param {string} value - Valor a validar
   * @param {RegExp} pattern - Patrón de validación
   * @returns {boolean} True si es válido
   */
  const validateField = (value, pattern) => {
    return pattern.test(value);
  };

  /**
   * Maneja la validación de cambios en los campos
   * @param {string} fieldName - Nombre del campo
   * @param {any} value - Valor del campo
   */
  const handleChangeValid = (fieldName, value) => {
    let isValid = true;

    if (fieldName === "isEnabled") {
      isValid = value !== undefined && value !== null;
    } else {
      isValid = validateField(value.trim(), validationPatterns[fieldName]);
    }

    setIsFieldValid((prevValidity) => ({
      ...prevValidity,
      [fieldName]: isValid,
    }));
  };

  /**
   * Verifica si hay campos inválidos
   * @param {Object} fields - Objeto con el estado de validación de los campos
   * @returns {boolean} True si hay campos inválidos
   */
  const hasInvalidFields = (fields) => {
    return Object.values(fields).some((value) => value === false);
  };

  /**
   * Maneja el cambio de página en la paginación
   * @param {Event} event - Evento de paginación
   * @param {number} value - Número de página
   */
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  /**
   * Maneja la apertura del modal de edición
   * @param {Object} subcategory - Datos de la subcategoría a editar
   */
  const handleSubCategoryEdit = (subcategory) => {
    resetFieldValid();
    setIsDisabled(false);
    setIsFormModified(false);
    setRecoveredData({
      ...subcategory,
      isEnabled: subcategory.isEnabled,
    });
    setEditedData({});
    setOpenEdit(true);
  };

  useEffect(() => {
    const hasFormChanged = Object.keys(editedData).some(
      (fieldName) => editedData[fieldName] !== recoveredData[fieldName]
    );

    const isAnyFieldEmpty = Object.values(editedData).some((value) => value === "");

    setIsFormModified(!isAnyFieldEmpty && hasFormChanged);
  }, [editedData]);

  /**
   * Envía los datos de edición al servidor
   * @param {Event} e - Evento del formulario
   */
  const submitSubCategoryEdit = async (e) => {
    e.preventDefault();
    const hasErrors = hasInvalidFields(isFieldValid);

    if (hasErrors) {
      handleAlert("fail", "Hay al menos un error en los campos ingresados");
      return;
    }

    setLoading(true);
    const payload = {
      name: editedData.name || recoveredData.name,
      isEnabled:
        editedData.isEnabled !== undefined ? editedData.isEnabled : recoveredData.isEnabled,
      category: categoryId,
    };

    const result = await SubCategoryUpdateService(recoveredData.id, payload);

    if (result.success) {
      await filterControllerRef.current.handleClickSubmit();
      handleAlert("success", "La subcategoría se ha actualizado correctamente");
    } else {
      handleAlert("fail", result.message);
    }

    closeSubCategoryEdit();
    setLoading(false);
  };

  /**
   * Maneja la apertura del modal de eliminación
   * @param {Object} subcategory - Datos de la subcategoría a eliminar
   */
  const handleSubCategoryDelete = (subcategory) => {
    setSubcategoryToDelete(subcategory);
    setOpenDelete(true);
  };

  /**
   * Confirma la eliminación de la subcategoría
   * @param {string} subcategoryId - ID de la subcategoría a eliminar
   */
  const confirmSubCategoryDelete = async (subcategoryId) => {
    setLoading(true);
    const result = await SubCategoryDeleteService(subcategoryId);

    if (result.success) {
      await filterControllerRef.current.handleClickSubmit();
      handleAlert("success", "La subcategoría se ha eliminado correctamente");
    } else {
      handleAlert("fail", result.message);
    }

    closeSubCategoryDelete();
    setSubcategoryToDelete(null);
    setLoading(false);
  };

  /**
   * Maneja la navegación de regreso a categorías
   */
  const handleGoBack = () => {
    navigate("/Categorias");
  };

  return (
    <>
      <Spinner loading={loading}></Spinner>
      <MDAlert
        isAlert={isAlert}
        setIsAlert={setIsAlert}
        typeAlert={typeAlert}
        message={message}></MDAlert>
      <FilterController
        filterConfig={filterConfig}
        dataTable={dataTable}
        ref={filterControllerRef}
        setTotalData={setTotalData}
        setEntriesEnd={setEntriesEnd}
        setEntriesStart={setEntriesStart}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        setTotalPages={setTotalPages}
        setListData={setSubCategoriesList}
        handleAlert={handleAlert}
        category_id={categoryId}
      />
      <SubCategoryList
        handleSubCategoryCreate={handleSubCategoryCreate}
        handleSubCategoryEdit={handleSubCategoryEdit}
        handleSubCategoryDelete={handleSubCategoryDelete}
        subCategoriesList={subCategoriesList}
        categoryName={categoryName}
        entriesStart={entriesStart}
        entriesEnd={entriesEnd}
        totalData={totalData}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        handleGoBack={handleGoBack}
      />
      <SubCategoryCreate
        open={openCreate}
        handleClose={closeSubCategoryCreate}
        getFieldValue={getFieldValueNew}
        handleFieldChange={handleFieldChangeNew}
        handleSubmit={submitSubCategoryCreate}
        isFieldValid={isFieldValid}
        categoryName={categoryName}
      />
      <SubCategoryEdit
        open={openEdit}
        handleClose={closeSubCategoryEdit}
        datosRecuperados={recoveredData}
        datosEditados={editedData}
        isFormModified={isFormModified}
        isDisabled={isDisabled}
        getFieldValue={getFieldValue}
        handleFieldChange={handleFieldChange}
        handleSubmit={submitSubCategoryEdit}
        isFieldValid={isFieldValid}
      />
      <SubCategoryDelete
        open={openDelete}
        handleClose={closeSubCategoryDelete}
        handleDelete={confirmSubCategoryDelete}
        subcategoryData={subcategoryToDelete}
        isDisabled={isDisabled}
      />
    </>
  );
}
